import type { OrgDirectoryAdapter } from '@zhiyue/core';

const normalizeText = (value: unknown) => String(value || '').trim();

/**
 * MemberPicker only caches names returned by its list request. Make sure the
 * prefilled member is included as well, since a tenant can have more members
 * than the first result page.
 */
export const createSelectedMemberAwareAdapter = (
  adapter: OrgDirectoryAdapter,
  selectedMemberUuid: string | null | undefined,
  selectedMemberName?: string | null,
): OrgDirectoryAdapter => {
  const safeSelectedMemberUuid = normalizeText(selectedMemberUuid);
  const safeSelectedMemberName = normalizeText(selectedMemberName);

  return {
    ...adapter,
    searchMembers: async (params) => {
      const result = await adapter.searchMembers(params);
      if (
        !safeSelectedMemberUuid ||
        result.list.some(
          (member) =>
            normalizeText(member.memberUuid) === safeSelectedMemberUuid,
        )
      ) {
        return result;
      }

      if (safeSelectedMemberName) {
        return {
          ...result,
          list: [
            {
              memberUuid: safeSelectedMemberUuid,
              name: safeSelectedMemberName,
            },
            ...result.list,
          ],
        };
      }

      if (!adapter.getMemberById) {
        return result;
      }

      const selectedMember = await adapter
        .getMemberById(safeSelectedMemberUuid)
        .catch(() => null);
      if (!selectedMember) {
        return result;
      }

      return {
        ...result,
        list: [selectedMember, ...result.list],
      };
    },
  };
};
